<template>
  <dialog
    ref="dialogRef"
    class="similar-search-dialog"
    aria-labelledby="similar-search-dialog-title"
    @cancel.prevent="handleClose"
    @click="handleBackdropClick"
  >
    <div class="similar-search-dialog__content">
      <div class="similar-search-dialog__header">
        <h2 id="similar-search-dialog-title" class="similar-search-dialog__title">
          かんたん検索<span class="similar-search-dialog__ai-tag" aria-hidden="true">AI</span>
        </h2>
        <button
          type="button"
          class="similar-search-dialog__close"
          aria-label="閉じる"
          @click="handleClose"
        >
          <i class="material-icons" aria-hidden="true">close</i>
        </button>
      </div>

      <div class="similar-search-dialog__body">
        <LoadingBar v-if="isLoading" />
        <template v-else>
          <div class="similar-search-dialog__input-area">
            <div v-if="!hasSearched && !hasError" class="similar-search-dialog__message-panel">
              <p>単語を並べなくて大丈夫です。<br>困っていることを、そのまま書いてください。<br>長い文章でも読み取って、文脈が似た質問を探します。</p>
            </div>
            <textarea
              id="similar-search-query"
              ref="queryInput"
              v-model="query"
              class="similar-search-dialog__textarea"
              placeholder="3歳の子どもが昨日の夜から38.5度の熱があります。今朝は少し下がって37.8度になりましたが、食欲がなく、ぐったりしています。咳や鼻水はありません。保育園で手足口病が流行っていると聞きました。すぐに病院に連れて行くべきでしょうか？それとも様子を見ても大丈夫でしょうか？"
              rows="8"
              :maxlength="VALIDATION.MAX_QUERY_LENGTH"
              @keydown.enter.ctrl="handleSearch"
            />
            <div class="similar-search-dialog__char-count">
              <span :class="{ 'similar-search-dialog__char-count--warning': isNearLimit }">
                {{ query.length }}
              </span>
              / {{ VALIDATION.MAX_QUERY_LENGTH }}
            </div>
            <button
              type="button"
              class="similar-search-dialog__search-btn"
              :disabled="!canSearch"
              @click="handleSearch"
            >
              検索する
            </button>
          </div>

          <div v-if="hasError" class="similar-search-dialog__error">
            <i class="material-icons" aria-hidden="true">error_outline</i>
            <p>検索できませんでした</p>
            <p class="similar-search-dialog__error-hint">
              しばらく時間をおいてから再度お試しください
            </p>
          </div>

          <div v-if="hasSearched" class="similar-search-dialog__results">
            <template v-if="results.length > 0">
              <p class="similar-search-dialog__results-count">
                {{ results.length }}件の似た質問が見つかりました
              </p>
              <ul class="similar-search-dialog__results-list" role="list">
                <li
                  v-for="(result, index) in results"
                  :key="result.topicId"
                  class="similar-search-dialog__result-item"
                >
                  <a
                    :href="result.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="similar-search-dialog__result-card"
                    @click="trackSimilarSearch('click', '結果カード')"
                  >
                    <div class="similar-search-dialog__result-header">
                      <h4 class="similar-search-dialog__result-title">
                        {{ result.title }}
                      </h4>
                      <RelevanceBadge :rank="index + 1" />
                    </div>
                    <p class="similar-search-dialog__result-snippet">
                      {{ result.snippet }}
                    </p>
                  </a>
                </li>
              </ul>
            </template>
            <template v-else>
              <div class="similar-search-dialog__no-results">
                <i class="material-icons" aria-hidden="true">search_off</i>
                <p>似た質問が見つかりませんでした</p>
                <p class="similar-search-dialog__no-results-hint">
                  違う言い方で入力してみてください
                </p>
              </div>
            </template>
          </div>
        </template>
      </div>

      <div class="similar-search-dialog__footer">
        <button
          type="button"
          class="similar-search-dialog__close-btn"
          @click="handleClose"
        >
          閉じる
        </button>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { searchSimilarTopics } from '../api';
import type { SimilarSearchResult } from '../types';
import { VALIDATION } from '../types';
import { trackSimilarSearch } from '../tracking';
import RelevanceBadge from './RelevanceBadge.vue';
import LoadingBar from './LoadingBar.vue';

const props = defineProps<{
  visible: boolean;
  initialQuery?: string;
}>();

const emit = defineEmits<{
  close: [];
}>();

const query = ref('');
const isLoading = ref(false);
const hasSearched = ref(false);
const hasError = ref(false);
const results = ref<SimilarSearchResult[]>([]);
const queryInput = ref<HTMLTextAreaElement | null>(null);
const dialogRef = ref<HTMLDialogElement | null>(null);

// 検索可能か（空でないこと）
const canSearch = computed(() => query.value.trim().length > 0);

// 文字数制限に近いか（残り20文字以下で警告）
const isNearLimit = computed(() => query.value.length >= VALIDATION.MAX_QUERY_LENGTH - 20);

const handleClose = () => {
  trackSimilarSearch('close', 'ダイアログ');
  dialogRef.value?.close();
  emit('close');
};

// backdrop クリック検出
const handleBackdropClick = (event: MouseEvent) => {
  if (event.target === dialogRef.value) {
    handleClose();
  }
};

const handleSearch = async () => {
  if (!canSearch.value || isLoading.value) return;

  trackSimilarSearch('search', 'ダイアログ');

  isLoading.value = true;
  hasSearched.value = false;
  hasError.value = false;

  try {
    const response = await searchSimilarTopics({ query: query.value.trim() });
    results.value = response.results;
    hasSearched.value = true;
  } catch (error) {
    console.error('検索エラー:', error);
    results.value = [];
    hasSearched.value = false;
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

// マウント時に visible=true なら showModal を呼ぶ
onMounted(async () => {
  if (props.visible) {
    dialogRef.value?.showModal();
    await nextTick();
    queryInput.value?.focus();
  }
});

// visible の変更を監視して showModal/close を呼ぶ
watch(
  () => props.visible,
  async (newVal) => {
    if (newVal) {
      // initialQuery を渡されて開くケースは相談文入力済みでの遷移とみなし、開いた時点で自動検索まで行う
      const hasInitialQuery = !!props.initialQuery?.trim();
      if (hasInitialQuery) {
        query.value = props.initialQuery!;
      }
      // 検索状態をリセット（入力内容は保持）
      hasSearched.value = false;
      hasError.value = false;
      results.value = [];

      dialogRef.value?.showModal();
      await nextTick();
      queryInput.value?.focus();

      if (hasInitialQuery) {
        void handleSearch();
      }
    } else {
      dialogRef.value?.close();
    }
  }
);
</script>

<style lang="scss" scoped>
@import '~green/_variables.scss';

.similar-search-dialog {
  border: none;
  padding: 0;
  max-width: 600px;
  width: calc(100% - 32px);
  max-height: calc(100vh - 48px);
  border-radius: 12px;
  background: transparent;

  // dialog 要素の showModal() は自動的に中央配置される
  // margin: auto で垂直・水平中央を保証
  margin: auto;

  &::backdrop {
    background: rgba(0, 0, 0, 0.6);
  }

  &__content {
    background: #fff;
    border-radius: 12px;
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid #eee;
  }

  &__title {
    font-size: 1.125rem;
    font-weight: bold;
    margin: 0;
    color: $base-font-color;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__ai-tag {
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    padding: 2px 4px;
    min-width: 32px;
    background: linear-gradient(to right, #28C8A0, #2898c8);
    color: $color-white;
    border-radius: 3px;
    text-align: center;
    box-sizing: border-box;
  }

  &__close {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    color: $base-font-color;

    &:hover {
      color: $primary-color;
    }
  }

  &__body {
    padding: 16px;
    flex: 1;
    overflow-y: auto;
  }

  &__input-area {
    margin-bottom: 16px;
  }

  &__message-panel {
    border-radius: $border-radius-base;
    margin-bottom: 16px;
    font-size: 0.875rem;
    line-height: 1.6;
    color: $base-font-color;

    p {
      margin: 0 0 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 1rem;
    resize: vertical;
    min-height: 200px;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: $primary-color;
      box-shadow: 0 0 0 2px rgba($primary-color, 0.2);
    }
  }

  &__char-count {
    text-align: right;
    font-size: 0.75rem;
    color: #999;
    margin-top: 4px;

    &--warning {
      color: #d32f2f;
      font-weight: bold;
    }
  }

  &__search-btn {
    width: 100%;
    margin-top: 12px;
    padding: 12px 24px;
    background-color: $primary-color;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover:not(:disabled) {
      background-color: darken($primary-color, 10%);
    }

    &:disabled {
      background-color: #ccc;
      cursor: not-allowed;
    }
  }

  &__results {
    margin-top: 16px;
  }

  &__results-count {
    font-size: 0.875rem;
    color: #666;
    margin-bottom: 12px;
  }

  &__results-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__result-item {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__result-card {
    display: block;
    background-color: #fff;
    border: 1px solid $base-border-color;
    border-radius: $border-radius-base;
    padding: 16px;
    text-decoration: none;
    color: inherit;
    transition: box-shadow 0.2s;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &:focus {
      outline: 2px solid $primary-color;
      outline-offset: 2px;
    }
  }

  &__result-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__result-title {
    flex: 1;
    font-size: 1rem;
    font-weight: bold;
    color: $base-font-color;
    margin: 0;
    line-height: 1.4;
  }

  &__result-snippet {
    font-size: 0.875rem;
    color: $tertiary-color;
    margin: 0;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__no-results {
    text-align: center;
    padding: 32px 16px;
    color: #666;

    .material-icons {
      font-size: 48px;
      margin-bottom: 16px;
      opacity: 0.5;
    }

    p {
      margin: 0 0 8px;
    }
  }

  &__no-results-hint {
    font-size: 0.875rem;
    color: #999;
  }

  &__error {
    text-align: center;
    padding: 32px 16px;
    color: #d32f2f;

    .material-icons {
      font-size: 48px;
      margin-bottom: 16px;
    }

    p {
      margin: 0 0 8px;
    }
  }

  &__error-hint {
    font-size: 0.875rem;
    color: #666;
  }

  &__footer {
    padding: 16px;
    border-top: 1px solid #eee;
    text-align: center;
  }

  &__close-btn {
    background: none;
    border: none;
    color: $base-font-color;
    font-size: 0.875rem;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: $primary-color;
    }
  }
}

// レスポンシブ対応: モバイルでは全画面表示
@media (max-width: 640px) {
  .similar-search-dialog {
    width: 100%;
    max-width: 100%;
    max-height: 100%;
    height: 100%;
    border-radius: 0;

    &__content {
      width: 100%;
      max-width: 100%;
      max-height: 100%;
      height: 100%;
      border-radius: 0;
    }
  }
}
</style>
